import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "components/primitives/Grid";
import NoDataWrapper from "components/NoDataWrapper";
import { exerciseAutocompleteFilterOptions } from "config/autocomplete";
import { Formik } from "formik";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import useExercises from "providers/ExercisesProvider/useExercises";
import Mui5 from "components/mui5";
import { useMemo, useState } from "react";
import * as Components from "./AthleteBulkEdit.components";
import useOneRepMaxFormInitialValues from "./hooks/useOneRepMaxFormInitialValues";
import MessageWithLogo from "components/MessageWithLogo";
import useBulkOneRepMaxUpdate from "./hooks/useBulkOneRepMaxUpdate";
import useAthleteStore from "providers/AthletesProvider/useAthleteStore";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Exercise } from "types/exercise";
import AthleteBulkEditFormFields from "./AthleteBulkEditFormFields";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";

const Container = styled(Grid)(({ theme }) => ({
  width: "100%",
  minHeight: theme.spacing(9),
  alignItems: "center",
  padding: theme.spacing(2, 3, 1, 3),
}));

interface AthleteBulkEditProps {
  athleteIds: string[];
  onCancel(): void;
}

function AthleteBulkEdit(props: AthleteBulkEditProps) {
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const exercises = useExercises();
  const visibleAthletes = useAthleteStore();
  const visibleAthleteIds = useMemo(() => {
    const idsOfVisibleAthletes = visibleAthletes.map(({ id }) => id);
    return props.athleteIds.filter((athleteId) => idsOfVisibleAthletes.includes(athleteId));
  }, [props.athleteIds, visibleAthletes]);

  const oneRepMaxExercises = useMemo(() => exercises.filter(ExerciseHelpers.isOneRepMaxExercise), [exercises]);
  const athletesOneRepMaxesInitialValues = useOneRepMaxFormInitialValues({
    exercise,
    athleteIds: visibleAthleteIds,
  });
  const { updateOneRepMaxes, loading } = useBulkOneRepMaxUpdate({ exercise });

  const spacingX = 3;
  const { t } = useTranslation();

  return (
    <Components.Paper>
      <Container container columns={12}>
        <Grid size={7}>
          <Typography variant="h2" sx={{ fontWeight: 400 }}>
            {t("athlete.bulk_edit1rm_header_title")}
          </Typography>
        </Grid>
        <Grid size={5} sx={{ paddingRight: 2.5 }}>
          <Mui5.Autocomplete
            disableClearable={!!exercise}
            value={exercise}
            options={oneRepMaxExercises}
            renderInput={(params: any) => (
              <Mui5.TextField
                {...params}
                margin="dense"
                label={t("athlete.bulk_edit1rm_autocomplete_title")}
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
              />
            )}
            groupBy={(option: Exercise) => option.category}
            getOptionLabel={(option: Exercise) => option.name}
            onChange={(_: any, exercise: Exercise | null) => setExercise(exercise)}
            disabled={!oneRepMaxExercises.length}
            filterOptions={exerciseAutocompleteFilterOptions}
          />
        </Grid>
      </Container>
      <IconButton
        sx={{ position: "absolute", top: 20, right: 4 }}
        onClick={props.onCancel}
        aria-label={t("global.close_label")}
      >
        <CloseIcon />
      </IconButton>
      <NoDataWrapper
        hasData={!!exercise}
        fallback={
          <>
            <Divider />
            <Box sx={{ textAlign: "center", px: spacingX, pt: 12 }}>
              <MessageWithLogo>{t("athlete.bulk_edit1rm_select_exercise_prompt")}</MessageWithLogo>
            </Box>
          </>
        }
      >
        <Formik
          enableReinitialize
          initialValues={{ athletesOneRepMaxes: athletesOneRepMaxesInitialValues }}
          onSubmit={updateOneRepMaxes}
        >
          <AthleteBulkEditFormFields
            spacingX={spacingX}
            exercise={exercise}
            onCancel={props.onCancel}
            loading={loading}
          />
        </Formik>
      </NoDataWrapper>
    </Components.Paper>
  );
}

export default AthleteBulkEdit;
